import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

const ImageLightbox = ({ images = [], currentIndex = 0, onIndexChange, onClose }) => {
  const allImages = Array.isArray(images) && images.length > 0 ? images : [];
  
  const [[page, direction], setPage] = React.useState([currentIndex, 0]);
  const [scale, setScale] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [lastTap, setLastTap] = React.useState(0);
  const pinchStartDistance = React.useRef(0);
  const initialScale = React.useRef(1);

  // Adjust page when currentIndex prop changes from parent (e.g. click thumbnail)
  useEffect(() => {
    if (page !== currentIndex) {
      const newDirection = currentIndex > page ? 1 : -1;
      setPage([currentIndex, newDirection]);
    }
  }, [currentIndex]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") moveNext();
      if (e.key === "ArrowLeft") movePrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "unset";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [page, allImages.length]);

  const moveNext = () => {
    if (allImages.length <= 1) return;
    const nextIndex = (page + 1) % allImages.length;
    setPage([nextIndex, 1]);
    onIndexChange(nextIndex);
  };

  const movePrev = () => {
    if (allImages.length <= 1) return;
    const prevIndex = (page - 1 + allImages.length) % allImages.length;
    setPage([prevIndex, -1]);
    onIndexChange(prevIndex);
  };

  // Reset scale and offset when switching images
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [page]);

  const handleDragEnd = (event, info) => {
    // Only swipe between images if not zoomed in
    if (scale <= 1.05) {
      const swipeThreshold = 50;
      const velocityThreshold = 500;
      if (info.offset.x < -swipeThreshold || info.velocity.x < -velocityThreshold) {
        moveNext();
      } else if (info.offset.x > swipeThreshold || info.velocity.x > velocityThreshold) {
        movePrev();
      }
    } else {
      // If zoomed, update offset after dragging
      setOffset(prev => ({
        x: prev.x + info.offset.x,
        y: prev.y + info.offset.y
      }));
    }
  };

  const handleDoubleTap = (e) => {
    e.stopPropagation();
    const now = Date.now();
    if (now - lastTap < 300) {
      if (scale > 1) {
        setScale(1);
        setOffset({ x: 0, y: 0 });
      } else {
        setScale(2.5);
      }
    }
    setLastTap(now);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartDistance.current = Math.sqrt(dx * dx + dy * dy);
      initialScale.current = scale;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStartDistance.current > 0) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const newScale = initialScale.current * (distance / pinchStartDistance.current);
      setScale(Math.max(1, Math.min(newScale, 4)));
    }
  };

  const handleTouchEnd = () => {
    pinchStartDistance.current = 0;
  };

  if (allImages.length === 0) return null;

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? "100%" : direction < 0 ? "-100%" : 0,
      opacity: 0,
      scale: 1,
    }),
    center: {
      zIndex: 1,
      x: offset.x,
      y: offset.y,
      opacity: 1,
      scale: scale,
      transition: {
        x: { type: "tween", ease: "easeOut", duration: 0.2 },
        y: { type: "tween", ease: "easeOut", duration: 0.2 },
        opacity: { duration: 0.2 },
        scale: { duration: 0.2 },
      }
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? "100%" : direction > 0 ? "-100%" : 0,
      opacity: 0,
      scale: 1,
      transition: {
        x: { type: "tween", ease: "easeIn", duration: 0.2 },
        opacity: { duration: 0.2 },
      }
    })
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/95 backdrop-blur-md overflow-hidden"
      onClick={onClose}
    >
      <div 
        className="relative w-full h-full flex items-center justify-center overflow-hidden" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all z-[130] bg-black/20 backdrop-blur-sm"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Navigation Buttons */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); movePrev(); }}
              className={`absolute left-4 sm:left-10 p-4 text-white hover:bg-white/10 rounded-full transition-all group z-[120] bg-black/10 backdrop-blur-sm hidden sm:flex ${scale > 1.1 ? "opacity-0 pointer-events-none" : "opacity-100"}`}
            >
              <svg className="w-10 h-10 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); moveNext(); }}
              className={`absolute right-4 sm:right-10 p-4 text-white hover:bg-white/10 rounded-full transition-all group z-[120] bg-black/10 backdrop-blur-sm hidden sm:flex ${scale > 1.1 ? "opacity-0 pointer-events-none" : "opacity-100"}`}
            >
              <svg className="w-10 h-10 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        <div 
          className="relative w-full h-full flex items-center justify-center overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={handleDoubleTap}
        >
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.img
              key={page}
              src={allImages[page]}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              drag={scale > 1 ? true : "x"}
              dragConstraints={scale > 1 ? false : { left: 0, right: 0 }}
              dragElastic={scale > 1 ? 0 : 0.1}
              onDragEnd={handleDragEnd}
              className={`absolute max-w-[95vw] max-h-[85vh] w-auto h-auto object-contain select-none ${scale > 1 ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing"}`}
            />
          </AnimatePresence>
        </div>

        {/* Indicators */}
        {allImages.length > 1 && (
          <div className={`absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 z-[120] transition-opacity duration-300 ${scale > 1.1 ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
            <div className="text-white font-bold bg-black/50 backdrop-blur-md px-6 py-2 rounded-full text-xs tracking-widest border border-white/10 uppercase">
              Ảnh {page + 1} / {allImages.length}
            </div>
            <div className="flex gap-1.5 sm:gap-2">
              {allImages.map((_, idx) => (
                <div 
                  key={idx}
                  className={`h-1 rounded-full transition-all duration-300 ${idx === page ? "w-6 bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]" : "w-1.5 bg-white/20"}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ImageLightbox;
